// Services > Channels > Queues — the tenant's surface for the human-handoff
// landing zones: create/rename/delete a queue, staff it from the agent
// directory, and tune how work is offered to that staff. It lives HERE, in the
// control-plane Application, not on the operator Desk: the Desk is the operator
// surface, administration is the Application's.
//
// The API is the real boundary — /api/services/queues reads for any member,
// writes only for a tenant admin (requireTenantAdmin + audit) — so this page's
// `admin` flag only decides which controls render, never what is allowed.
//
// green/amber/red carry agent PRESENCE, never actions. Agents show the
// JWT-projected display name channels stamped on agent_state, falling back to a
// short stable id for an agent whose token carried none.
import { useContext, useEffect, useState } from 'react'
import { isTenantAdmin, AuthContext } from '@retink/web-auth'
import { Button, ConfirmDestructive, Field, Input, PageHeader, EmptyState, Badge, useResource, toast } from '@retink/web-ui'
import { Plus, Users, Trash2, UserPlus, X, Inbox, ChevronRight, ChevronDown, Route } from 'lucide-react'
import {
  queuesAdmin,
  type Queue, type QueueMember, type AgentDirectoryEntry, type RoutingConfig, type Presence,
  type AgentConversation,
} from '../lib/queues-api'

// One place turns a failed mutation into an operator-visible message.
function handleApiError(e: unknown) {
  toast.error((e as Error)?.message || 'Something went wrong')
}

const selectClass =
  'h-9 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

// Presence → dot colour, the SAME mapping the Desk topbar uses (green online /
// amber away / red busy / muted offline). null = a member with no agent_state
// row (staffed but never signed into the Desk).
const DOT: Record<Presence, string> = {
  online: 'bg-[var(--success)]',
  away: 'bg-[var(--warning)]',
  busy: 'bg-[var(--destructive)]',
  offline: 'bg-muted-foreground',
}
function presenceDot(p: Presence | null): string {
  return p ? DOT[p] : 'bg-border'
}
function presenceLabel(p: Presence | null): string {
  return p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Not signed in'
}

// The JWT-projected display name (stamped on agent_state at heartbeat/status),
// falling back to a short, stable id for an agent whose token carried no name.
function agentLabel(a: { displayName: string | null; userId: string }): string {
  return a.displayName ?? a.userId.slice(0, 8)
}

function ago(ts: string | null): string | null {
  if (!ts) return null
  const mins = Math.round((Date.now() - Date.parse(ts)) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`
  return `${Math.round(mins / 1440)}d ago`
}

// A URL-safe slug — the queue key referenced by flows as Dispatch(human,
// queue:<key>). Immutable after create, so we derive a suggestion from the name.
function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)
}

export default function Queues() {
  const { user } = useContext(AuthContext)
  // Reads are open to every member (the server says so); only the mutating
  // controls are an admin's. A member sees the surface instead of a redirect.
  const admin = isTenantAdmin(user)

  const queuesRes = useResource(() => queuesAdmin.list(), [])
  const agentsRes = useResource(() => queuesAdmin.agents(), [])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const queues = queuesRes.data ?? []
  const selected = queues.find((q) => q.id === selectedId) ?? null

  const reload = () => { queuesRes.reload(); agentsRes.reload() }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <PageHeader
          title="Queues"
          description="Named landing zones for human handoffs. Create a queue, staff it with agents, and reference it from a flow's Dispatch step."
          action={admin ? (
            <Button onClick={() => { setCreating(true); setSelectedId(null) }}>
              <Plus /> New queue
            </Button>
          ) : undefined}
        />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="flex flex-col gap-2">
            {creating && (
              <CreateQueue
                onCancel={() => setCreating(false)}
                onCreated={(q) => { setCreating(false); reload(); setSelectedId(q.id) }}
              />
            )}
            {queuesRes.loading && <div className="text-sm text-muted-foreground">Loading queues…</div>}
            {!queuesRes.loading && queues.length === 0 && !creating && (
              <EmptyState icon={Inbox} title="No queues yet" description="Create a queue, then staff it and point a flow's handoff at it." />
            )}
            {queues.map((q) => (
              <button
                key={q.id}
                onClick={() => { setSelectedId(q.id); setCreating(false) }}
                className={`flex items-center gap-3 rounded-[10px] border px-4 py-3 text-left transition-colors ${
                  selectedId === q.id ? 'border-accent-border bg-muted' : 'border-border bg-card hover:bg-muted'
                }`}
              >
                <Inbox className="size-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-foreground">{q.name}</span>
                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{q.key}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{q.members} {q.members === 1 ? 'agent' : 'agents'}</span>
                    <span className={q.offerable > 0 ? 'text-[var(--success)]' : ''}>· {q.offerable} available</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div>
            {selected ? (
              <QueueDetail
                key={selected.id}
                queue={selected}
                agents={agentsRes.data ?? []}
                admin={admin}
                onChanged={reload}
                onDeleted={() => { setSelectedId(null); reload() }}
              />
            ) : (
              <div className="rounded-[10px] border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                Select a queue to see its staffing and routing.
              </div>
            )}
          </div>
        </div>

        <Workforce agents={agentsRes.data ?? []} admin={admin} onChanged={reload} />
      </div>
    </div>
  )
}

// ─── Workforce: every agent's live state, their capacity knob, and the
// force-close lever for a conversation pinning a slot. Presence stays the
// agent's own declaration in the Desk — the admin tunes capacity only.
function Workforce({ agents, admin, onChanged }: {
  agents: AgentDirectoryEntry[]
  admin: boolean
  onChanged: () => void
}) {
  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center gap-2">
        <Users className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Workforce</h2>
        <span className="text-xs text-muted-foreground">
          Live agent state. Capacity is how many conversations an agent holds at once; at capacity, new offers pass them by.
        </span>
      </div>
      {agents.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No agents yet — an agent appears here after their first Desk sign-in.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {agents.map((a) => (
            <WorkforceRow key={a.userId} agent={a} admin={admin} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  )
}

function WorkforceRow({ agent, admin, onChanged }: {
  agent: AgentDirectoryEntry
  admin: boolean
  onChanged: () => void
}) {
  const [open, setOpen] = useState(false)
  const [capacity, setCapacity] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [convs, setConvs] = useState<AgentConversation[] | null>(null)
  const [closing, setClosing] = useState<AgentConversation | null>(null)
  const atCapacity = agent.capacity > 0 && agent.inflight >= agent.capacity

  async function loadConvs() {
    try { setConvs(await queuesAdmin.agentConversations(agent.userId)) } catch (e) { handleApiError(e) }
  }

  async function toggle() {
    const next = !open
    setOpen(next)
    if (next && convs === null) await loadConvs()
  }

  async function saveCapacity() {
    const n = Number(capacity)
    if (!Number.isFinite(n)) return
    setBusy(true)
    try {
      await queuesAdmin.setAgentCapacity(agent.userId, Math.max(0, Math.min(100, Math.round(n))))
      setCapacity('')
      onChanged()
    } catch (e) {
      handleApiError(e)
    } finally {
      setBusy(false)
    }
  }

  async function forceClose(conv: AgentConversation) {
    try {
      await queuesAdmin.adminCloseConversation(conv.conversationId, 'released by admin')
      toast.success('Conversation closed — the slot is free')
      await loadConvs()
      onChanged()
    } catch (e) {
      handleApiError(e)
    }
  }

  return (
    <div className="rounded-[10px] border border-border bg-card">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={toggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" type="button">
          {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
          <span className={`h-2 w-2 shrink-0 rounded-full ${presenceDot(agent.presence)}`} title={presenceLabel(agent.presence)} />
          <span className={`min-w-0 truncate text-sm text-foreground ${agent.displayName ? '' : 'font-mono'}`}>{agentLabel(agent)}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{presenceLabel(agent.presence)}</span>
        </button>
        <Badge tone={atCapacity ? 'danger' : 'outline'} className="shrink-0 font-mono tabular-nums">
          {agent.inflight}/{agent.capacity}{atCapacity ? ' · at capacity' : ''}
        </Badge>
        <span className="shrink-0 text-[11px] text-muted-foreground">{ago(agent.lastSeenAt)}</span>
        {admin && (
          <span className="flex shrink-0 items-center gap-1">
            <Input
              type="number" inputMode="numeric" disabled={busy}
              placeholder={String(agent.capacity)}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className="h-8 w-20"
              aria-label="Capacity"
            />
            <Button variant="outline" onClick={saveCapacity} disabled={busy || capacity === ''}>Set</Button>
          </span>
        )}
      </div>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {convs === null ? (
            <div className="text-xs text-muted-foreground">Loading conversations…</div>
          ) : convs.length === 0 ? (
            <div className="text-xs text-muted-foreground">No open conversations — nothing holds a slot.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {convs.map((cv) => (
                <div key={cv.conversationId} className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{cv.channel}</span>
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">{cv.conversationId}</span>
                  {cv.handoffState && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{cv.handoffState}</span>}
                  <span className="shrink-0 text-[11px] text-muted-foreground" title="Time since last activity">idle {ago(cv.updatedAt)}</span>
                  {admin && (
                    <Button variant="outline" onClick={() => setClosing(cv)}>Force close</Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {closing && (
            <ConfirmDestructive
              open={!!closing}
              onOpenChange={(o) => { if (!o) setClosing(null) }}
              title="Force-close this conversation?"
              description={
                <>
                  This closes the conversation for the customer and frees the agent's slot. The transcript is kept.
                  Type <strong>{closing.conversationId.slice(0, 8)}</strong> to confirm.
                </>
              }
              confirmPhrase={closing.conversationId.slice(0, 8)}
              confirmLabel="Force close"
              onConfirm={async () => { const cv = closing; setClosing(null); if (cv) await forceClose(cv) }}
            />
          )}
        </div>
      )}
    </div>
  )
}

function CreateQueue({ onCancel, onCreated }: { onCancel: () => void; onCreated: (q: Queue) => void }) {
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const effectiveKey = keyTouched ? key : slugify(name)

  async function create() {
    if (!name.trim() || !effectiveKey) return
    setSaving(true)
    try {
      const q = await queuesAdmin.create({ key: effectiveKey, name: name.trim() })
      onCreated(q)
    } catch (e) {
      handleApiError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-[10px] border border-accent-border/40 bg-card p-4">
      <div className="text-sm font-medium text-foreground">New queue</div>
      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Support" disabled={saving} autoFocus />
      </Field>
      <Field label="Key (referenced by flows — immutable)">
        <Input
          value={effectiveKey}
          onChange={(e) => { setKeyTouched(true); setKey(slugify(e.target.value)) }}
          placeholder="support"
          disabled={saving}
        />
      </Field>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={create} disabled={saving || !name.trim() || !effectiveKey}>{saving ? 'Creating…' : 'Create'}</Button>
      </div>
    </div>
  )
}

function QueueDetail({
  queue, agents, admin, onChanged, onDeleted,
}: {
  queue: Queue
  agents: AgentDirectoryEntry[]
  admin: boolean
  onChanged: () => void
  onDeleted: () => void
}) {
  const membersRes = useResource(() => queuesAdmin.members(queue.id), [queue.id])
  const [name, setName] = useState(queue.name)
  const [busy, setBusy] = useState(false)
  const [addUserId, setAddUserId] = useState('')

  useEffect(() => { setName(queue.name) }, [queue.name])

  const members = membersRes.data ?? []
  const memberIds = new Set(members.map((m) => m.userId))
  const candidates = agents.filter((a) => !memberIds.has(a.userId))
  const nameChanged = name.trim() !== queue.name && name.trim().length > 0

  async function rename() {
    if (!nameChanged) return
    setBusy(true)
    try {
      await queuesAdmin.rename(queue.id, name.trim())
      onChanged()
    } catch (e) {
      handleApiError(e)
    } finally {
      setBusy(false)
    }
  }

  // Destructive delete is type-to-confirm (house rule): fetch the impact first,
  // then arm the ConfirmDestructive dialog with it.
  const [confirmDelete, setConfirmDelete] = useState<{ waiting: number } | null>(null)

  async function askRemove() {
    setBusy(true)
    try {
      const { waiting } = await queuesAdmin.impact(queue.id)
      setConfirmDelete({ waiting })
    } catch (e) {
      handleApiError(e)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    try {
      await queuesAdmin.remove(queue.id)
      onDeleted()
    } catch (e) {
      handleApiError(e)
    }
  }

  async function addMember() {
    if (!addUserId) return
    setBusy(true)
    try {
      await queuesAdmin.addMember(queue.id, addUserId)
      setAddUserId('')
      membersRes.reload()
      onChanged()
    } catch (e) {
      handleApiError(e)
    } finally {
      setBusy(false)
    }
  }

  async function removeMember(userId: string) {
    setBusy(true)
    try {
      await queuesAdmin.removeMember(queue.id, userId)
      membersRes.reload()
      onChanged()
    } catch (e) {
      handleApiError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 rounded-[10px] border border-border bg-card p-5">
      {/* Rename + delete */}
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <Field label="Name" className="flex-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={busy || !admin} />
          </Field>
          {admin && <Button variant="outline" onClick={rename} disabled={busy || !nameChanged}>Rename</Button>}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs text-muted-foreground">key: {queue.key}</span>
          {admin && <Button variant="destructive" onClick={askRemove} disabled={busy}><Trash2 /> Delete queue</Button>}
          <ConfirmDestructive
            open={confirmDelete !== null}
            onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}
            title="Delete queue"
            description={
              confirmDelete && confirmDelete.waiting > 0
                ? `${confirmDelete.waiting} conversation${confirmDelete.waiting === 1 ? ' is' : 's are'} waiting unclaimed in this queue and will be stranded. Flows referencing "${queue.key}" will fail to dispatch here.`
                : `Flows referencing "${queue.key}" will fail to dispatch here.`
            }
            confirmPhrase={queue.name}
            confirmLabel="Delete queue"
            onConfirm={remove}
          />
        </div>
      </div>

      {/* Staffing */}
      <div className="border-t border-border pt-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
          <Users className="size-4 text-muted-foreground" /> Staffing
          <span className="text-xs font-normal text-muted-foreground">{members.length} {members.length === 1 ? 'agent' : 'agents'}</span>
        </div>

        {/* Add from the directory */}
        {admin && <div className="mb-3 flex items-center gap-2">
          <select className={selectClass} value={addUserId} onChange={(e) => setAddUserId(e.target.value)} disabled={busy}>
            <option value="">{candidates.length ? 'Add an agent…' : 'No agents available to add'}</option>
            {candidates.map((a) => (
              <option key={a.userId} value={a.userId}>
                {agentLabel(a)} · {presenceLabel(a.presence)}{a.queues > 0 ? ` · in ${a.queues} queue${a.queues === 1 ? '' : 's'}` : ''}
              </option>
            ))}
          </select>
          <Button variant="outline" onClick={addMember} disabled={busy || !addUserId}><UserPlus /> Add</Button>
        </div>}

        {membersRes.loading && <div className="text-sm text-muted-foreground">Loading members…</div>}
        {!membersRes.loading && members.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
            No agents staffed. Only agents who have signed into the Desk appear in the directory.
          </div>
        )}
        <div className="flex flex-col gap-2">
          {members.map((m) => (
            <MemberRow key={m.userId} member={m} disabled={busy || !admin} admin={admin} onRemove={() => removeMember(m.userId)} />
          ))}
        </div>
      </div>

      {/* Routing — the offer/availability knobs (voice.md RULED 2026-07-11). */}
      <QueueRouting queueId={queue.id} admin={admin} />

      {agents.length === 0 && (
        <p className="text-xs text-muted-foreground">
          The agent directory is empty. Agents appear here once they have signed into the Desk at least once.
        </p>
      )}
    </div>
  )
}

// The per-queue Routing section: offer window, rotation, re-ring, exhaustion, and
// wrap-up grace. Collapsed by default (one queue's tuning shouldn't fetch config
// for every row). Empty field = inherit the platform default (shown as the
// placeholder); only the fields the admin fills persist.
function QueueRouting({ queueId, admin }: { queueId: string; admin: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t border-border pt-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-sm font-medium text-foreground"
      >
        {open ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
        <Route className="size-4 text-muted-foreground" /> Routing
        <span className="text-xs font-normal text-muted-foreground">allocation order, offer window, exhaustion, sticky agent</span>
      </button>
      {open && <RoutingForm key={queueId} queueId={queueId} admin={admin} />}
    </div>
  )
}

function RoutingForm({ queueId, admin }: { queueId: string; admin: boolean }) {
  const res = useResource(() => queuesAdmin.getRouting(queueId), [queueId])
  const [form, setForm] = useState<RoutingConfig>({})
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (res.data) setForm(res.data.routing) }, [res.data])

  if (res.loading) return <div className="pt-3 text-sm text-muted-foreground">Loading routing…</div>
  if (!res.data) return null
  const d = res.data.defaults
  const ro = saving || !admin

  const setNum = (key: keyof RoutingConfig, raw: string) =>
    setForm((f) => ({ ...f, [key]: raw === '' ? undefined : Number(raw) }))

  async function save() {
    setSaving(true)
    try {
      await queuesAdmin.putRouting(queueId, form)
      res.reload()
    } catch (e) {
      handleApiError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 pt-3">
      {/* Two delivery modes coexist and are deliberately named as such: voice
          offers are PUSHED to one agent at a time by the server (ring), while
          Desk always keeps the queue view open to PULL (claim). Exhaustion
          decides which one a stuck conversation falls back to. */}
      <p className="text-xs text-muted-foreground">
        Delivery: a live voice call is <strong>pushed</strong> — the server rings offerable agents in this
        order and the first to accept takes it. Everything else is also <strong>pullable</strong> — it waits
        in the Desk queue view for any agent staffing this queue to claim. These settings apply to the whole
        queue; per-channel overrides are not modelled yet.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Offer window (seconds)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.offerSeconds}`}
            value={form.offerSeconds ?? ''} onChange={(e) => setNum('offerSeconds', e.target.value)}
          />
        </Field>
        <Field label="Allocation order">
          <select
            className={selectClass} disabled={ro}
            value={form.rotation ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, rotation: (e.target.value || undefined) as RoutingConfig['rotation'] }))}
          >
            <option value="">Default ({d.rotation === 'ring_all' ? 'ring all' : 'sequential'})</option>
            <option value="sequential">Sequential — one agent at a time</option>
            <option value="ring_all">Ring all — every free agent at once</option>
          </select>
        </Field>
        <Field label="Re-ring cycles (0 = unlimited)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.reRingCycles}`}
            value={form.reRingCycles ?? ''} onChange={(e) => setNum('reRingCycles', e.target.value)}
          />
        </Field>
        <Field label="Re-ring cooldown (seconds)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.reRingCooldownSeconds}`}
            value={form.reRingCooldownSeconds ?? ''} onChange={(e) => setNum('reRingCooldownSeconds', e.target.value)}
          />
        </Field>
        <Field label="When no one answers">
          <select
            className={selectClass} disabled={ro}
            value={form.exhaustionAction ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, exhaustionAction: (e.target.value || undefined) as RoutingConfig['exhaustionAction'] }))}
          >
            <option value="">Default ({d.exhaustionAction === 'voicemail' ? 'voicemail' : 'stay in queue'})</option>
            <option value="stay_awaiting">Stay in queue (pull model)</option>
            <option value="voicemail">Send caller to voicemail</option>
          </select>
        </Field>
        {form.exhaustionAction === 'voicemail' && (
          <Field label="Voicemail after (seconds)">
            <Input
              type="number" inputMode="numeric" disabled={ro}
              placeholder={`default ${d.exhaustionVoicemailAfterSeconds}`}
              value={form.exhaustionVoicemailAfterSeconds ?? ''}
              onChange={(e) => setNum('exhaustionVoicemailAfterSeconds', e.target.value)}
            />
          </Field>
        )}
        <Field label="Wrap-up grace (seconds)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.wrapupGraceSeconds}`}
            value={form.wrapupGraceSeconds ?? ''} onChange={(e) => setNum('wrapupGraceSeconds', e.target.value)}
          />
        </Field>
        <Field label="Chat no-claim timeout (seconds, 0 = off)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.noClaimSeconds}`}
            value={form.noClaimSeconds ?? ''} onChange={(e) => setNum('noClaimSeconds', e.target.value)}
          />
        </Field>
        <Field label="Chat no-claim message">
          <Input
            type="text" disabled={ro}
            placeholder={d.noClaimMessage}
            value={form.noClaimMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, noClaimMessage: e.target.value || undefined }))}
          />
        </Field>
        <Field label="Claimed-chat idle close (seconds, 0 = off)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.chatIdleCloseSeconds}`}
            value={form.chatIdleCloseSeconds ?? ''} onChange={(e) => setNum('chatIdleCloseSeconds', e.target.value)}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            A web chat an agent has claimed closes after this much total silence — the agent's slot frees up.
          </p>
        </Field>
        <Field label="Close after visitor leaves (seconds, 0 = off)">
          <Input
            type="number" inputMode="numeric" disabled={ro}
            placeholder={`default ${d.chatVisitorLeftCloseSeconds}`}
            value={form.chatVisitorLeftCloseSeconds ?? ''} onChange={(e) => setNum('chatVisitorLeftCloseSeconds', e.target.value)}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            When the visitor's browser reports the page closed, the chat closes after this much further silence.
          </p>
        </Field>
      </div>
      {/* Sticky agent (#61) — a tri-state: unset inherits the platform default,
          so "off" has to be storable separately from "not set". */}
      <Field label="Sticky agent (SMS / WhatsApp)">
        <select
          className={selectClass} disabled={ro}
          value={form.stickyAgent === undefined ? '' : String(form.stickyAgent)}
          onChange={(e) => setForm((f) => ({ ...f, stickyAgent: e.target.value === '' ? undefined : e.target.value === 'true' }))}
        >
          <option value="">Default ({d.stickyAgent ? 'on' : 'off'})</option>
          <option value="true">On — offer to whoever handled this customer before, first</option>
          <option value="false">Off — order by availability only</option>
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          A preference, not a reservation: a preferred agent who isn’t free is skipped, never waited for.
        </p>
      </Field>
      <div className="flex items-center justify-end">
        <Button onClick={save} disabled={ro}>{saving ? 'Saving…' : 'Save routing'}</Button>
      </div>
    </div>
  )
}

function MemberRow({ member, disabled, admin, onRemove }: { member: QueueMember; disabled: boolean; admin: boolean; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
      <span className={`h-2 w-2 shrink-0 rounded-full ${presenceDot(member.presence)}`} title={presenceLabel(member.presence)} />
      <span className={`min-w-0 flex-1 truncate text-sm text-foreground ${member.displayName ? '' : 'font-mono'}`}>{agentLabel(member)}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{presenceLabel(member.presence)}</span>
      {member.presence != null && member.capacity != null && (
        <Badge tone="outline" className="shrink-0 font-mono tabular-nums">{member.inflight}/{member.capacity}</Badge>
      )}
      {member.lastSeenAt && <span className="shrink-0 text-[11px] text-muted-foreground">{ago(member.lastSeenAt)}</span>}
      {admin && (
        <button
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove from queue"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-[var(--destructive)] disabled:opacity-50"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}
